import { z } from 'zod';
import { KB_ARTICLE_STATUSES, STAFF_ROLES } from '@bulk/types';
import { idSchema, patchOf, searchQuerySchema } from './common';

const staffRoleSchema = z.enum(STAFF_ROLES);

const kbTitleSchema = z
  .object({ en: z.string().trim().max(160).default(''), ka: z.string().trim().max(160).default('') })
  .refine((t) => t.en !== '' || t.ka !== '', { message: 'Give the title in at least one language' });

const kbBodySchema = z.object({ en: z.string().max(40000).default(''), ka: z.string().max(40000).default('') });

const kbNameSchema = z
  .object({ en: z.string().trim().max(80).default(''), ka: z.string().trim().max(80).default('') })
  .refine((t) => t.en !== '' || t.ka !== '', { message: 'Give the name in at least one language' });

const localizedSchema = z.object({ en: z.string(), ka: z.string() });

export const createKbCategorySchema = z.object({
  name: kbNameSchema,
  sortOrder: z.number().int().min(0).max(1000).default(0),
});
export type CreateKbCategoryInput = z.infer<typeof createKbCategorySchema>;

export const updateKbCategorySchema = patchOf(createKbCategorySchema);
export type UpdateKbCategoryInput = z.infer<typeof updateKbCategorySchema>;

export const kbCategorySchema = z.object({
  id: idSchema,
  name: localizedSchema,
  sortOrder: z.number().int(),
  articleCount: z.number().int().describe('The articles in it that the caller can open'),
});
export type KbCategoryDto = z.infer<typeof kbCategorySchema>;

export const createKbArticleSchema = z.object({
  categoryId: idSchema.nullable().default(null),
  title: kbTitleSchema,
  body: kbBodySchema.default({ en: '', ka: '' }),
  audienceRoles: z.array(staffRoleSchema).max(STAFF_ROLES.length).default([]).describe('The gym roles it is for; empty means every staff role'),
  mustRead: z.boolean().default(false).describe('Ask the staff it is for to confirm they read the published version'),
});
export type CreateKbArticleInput = z.infer<typeof createKbArticleSchema>;

export const updateKbArticleSchema = patchOf(createKbArticleSchema);
export type UpdateKbArticleInput = z.infer<typeof updateKbArticleSchema>;

export const kbArticleListQuerySchema = searchQuerySchema.extend({
  categoryId: idSchema.optional(),
  status: z.enum(KB_ARTICLE_STATUSES).optional().describe('Drafts are listed only to managers (kb.manage)'),
  mustRead: z.stringbool().optional(),
  awaitingMe: z.stringbool().optional().describe('Only the required articles the caller still has to confirm'),
  sort: z.enum(['recent', 'title']).default('recent'),
});
export type KbArticleListQuery = z.infer<typeof kbArticleListQuerySchema>;

export const kbArticleSummarySchema = z.object({
  id: idSchema,
  categoryId: idSchema.nullable(),
  title: localizedSchema,
  status: z.enum(KB_ARTICLE_STATUSES),
  audienceRoles: z.array(staffRoleSchema),
  mustRead: z.boolean(),
  version: z.number().int(),
  updatedAt: z.string().describe('Managers see the last edit of the draft, everyone else the last publish'),
  updatedByName: z.string().nullable(),
  hasUnpublishedChanges: z.boolean().describe('Always false unless the caller holds kb.manage'),
  confirmationNeeded: z.boolean().describe('A required article for the caller’s role that the caller has not confirmed at its current version'),
});
export type KbArticleSummaryDto = z.infer<typeof kbArticleSummarySchema>;

const kbContentSchema = z.object({ title: localizedSchema, body: localizedSchema });

export const KB_IMAGES_MAX = 50;

const KB_IMAGE_REF = /kb-image:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/g;

/** The pictures a piece of article Markdown refers to as `kb-image:<id>`. */
export function kbImageIds(...texts: string[]): string[] {
  return [...new Set(texts.flatMap((text) => [...text.matchAll(KB_IMAGE_REF)].map((match) => match[1]!)))];
}

/** The Markdown with each `kb-image:<id>` swapped for its signed link; a reference with no link is left as it is. */
export function resolveKbImages(text: string, urls: ReadonlyMap<string, string>): string {
  return text.replace(KB_IMAGE_REF, (ref, id: string) => urls.get(id) ?? ref);
}

export const KB_CAPTION_MAX = 300;
export const KB_ALT_TEXT_MAX = 200;

export const describeKbImageSchema = z.object({
  caption: z.object({ en: z.string().trim().max(KB_CAPTION_MAX).default(''), ka: z.string().trim().max(KB_CAPTION_MAX).default('') }).describe('Shown under the picture, in each language; empty for none'),
  altText: z.object({ en: z.string().trim().max(KB_ALT_TEXT_MAX).default(''), ka: z.string().trim().max(KB_ALT_TEXT_MAX).default('') }).describe('What a screen reader says for the picture, in each language; empty for none'),
});
export const updateKbImageSchema = patchOf(describeKbImageSchema);
export type UpdateKbImageInput = z.infer<typeof updateKbImageSchema>;

export const kbImageSchema = z.object({
  id: idSchema,
  fileName: z.string(),
  url: z.string().describe('A signed link that expires after a few minutes'),
  caption: localizedSchema,
  altText: localizedSchema,
});
export type KbImageDto = z.infer<typeof kbImageSchema>;

export const kbArticleSchema = kbArticleSummarySchema.extend({
  published: kbContentSchema.nullable().describe('What readers see; null while the article is a draft'),
  draft: kbContentSchema.nullable().describe('The working copy, sent only to managers (kb.manage)'),
  publishedAt: z.string().nullable(),
  confirmedAt: z.string().nullable().describe('When the caller confirmed the current version'),
  images: z.array(kbImageSchema).describe('Signed links for the pictures the published article shows; managers get every picture of the article'),
});
export type KbArticleDto = z.infer<typeof kbArticleSchema>;

export const kbRevisionSchema = z.object({
  version: z.number().int(),
  title: localizedSchema,
  body: localizedSchema,
  publishedAt: z.string(),
  publishedByName: z.string().nullable(),
});
export type KbRevisionDto = z.infer<typeof kbRevisionSchema>;

export const restoreKbRevisionSchema = z.object({ version: z.number().int().min(1) });
export type RestoreKbRevisionInput = z.infer<typeof restoreKbRevisionSchema>;

export const kbReaderSchema = z.object({
  userId: idSchema,
  name: z.string(),
  email: z.string(),
  role: staffRoleSchema,
  confirmedAt: z.string().nullable().describe('When they confirmed the current version; null if they have not'),
  confirmedVersion: z.number().int().nullable().describe('The latest version they confirmed, if any'),
});
export type KbReaderDto = z.infer<typeof kbReaderSchema>;

export const kbReadersSchema = z.object({
  version: z.number().int(),
  required: z.number().int(),
  confirmed: z.number().int(),
  people: z.array(kbReaderSchema),
});
export type KbReadersDto = z.infer<typeof kbReadersSchema>;

export const kbSettingsSchema = z.object({
  remindUnread: z.boolean().default(true).describe('Remind staff who have not confirmed a required article'),
  remindAfterDays: z.number().int().min(1).max(30).default(3).describe('Days after an article is published before the reminder goes out'),
});
export type KbSettings = z.infer<typeof kbSettingsSchema>;
export const updateKbSettingsSchema = patchOf(kbSettingsSchema);
export type UpdateKbSettingsInput = z.infer<typeof updateKbSettingsSchema>;
